import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Filter,
  SortDesc,
  Search,
  X,
  ChevronDown,
  Download,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { orderAPI } from '@services/api';
import { formatCurrency, formatDate, formatRelativeDate } from '@utils/helpers';
import { ROUTES } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Alert from '@components/common/Alert';
import clsx from 'clsx';
import type { OrderHistoryItem, OrderHistoryFilters, PaginationMeta, OrderItem } from '@/types';

type OrderStatus = 'pending' | 'paid' | 'processing' | 'completed' | 'cancelled';

const OrderHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [orders, setOrders] = useState<OrderHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [downloadingPhotoId, setDownloadingPhotoId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Filters
  const [filters, setFilters] = useState<OrderHistoryFilters>({
    status: undefined,
    startDate: undefined,
    endDate: undefined,
    eventId: undefined,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<'createdAt' | 'total'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(ROUTES.LOGIN, { state: { from: ROUTES.ORDER_HISTORY } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch orders
  const fetchOrders = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await orderAPI.getOrderHistory({
        ...filters,
        page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
      });

      setOrders(response.data.orders || []);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order history');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders(1);
    }
  }, [isAuthenticated, filters, sortBy, sortOrder]);

  // Handle download
  const handleDownloadPhoto = async (orderId: string, photoId: string, itemTitle: string) => {
    setDownloadingPhotoId(photoId);
    try {
      const blob = await orderAPI.downloadPhoto(orderId, photoId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${itemTitle}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download photo. Please try again.');
    } finally {
      setDownloadingPhotoId(null);
    }
  };

  // Handle filter change
  const handleFilterChange = (key: keyof OrderHistoryFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: undefined,
      startDate: undefined,
      endDate: undefined,
      eventId: undefined,
    });
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Toggle sort order
  const toggleSort = (field: 'createdAt' | 'total') => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= pagination.pages) {
      fetchOrders(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    const badgeConfig: Record<OrderStatus, any> = {
      pending: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: <Clock size={16} className="text-yellow-600" />,
        label: 'Pending',
      },
      paid: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: <Package size={16} className="text-blue-600" />,
        label: 'Paid',
      },
      processing: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-800',
        icon: <Clock size={16} className="text-purple-600" />,
        label: 'Processing',
      },
      completed: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-800',
        icon: <CheckCircle size={16} className="text-green-600" />,
        label: 'Completed',
      },
      cancelled: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: <XCircle size={16} className="text-red-600" />,
        label: 'Cancelled',
      },
    };

    const config = badgeConfig[status];
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bg} ${config.border}`}
      >
        {config.icon}
        <span className={`text-sm font-medium ${config.text}`}>{config.label}</span>
      </div>
    );
  };

  const isOrderDownloadable = (order: OrderHistoryItem): boolean => {
    return order.status === 'completed' || order.status === 'paid';
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== undefined).length;

  if (authLoading || (loading && orders.length === 0)) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="flex flex-col items-center justify-center">
              <LoadingSpinner size="large" />
              <p className="text-gray-500 mt-4">Loading your orders...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShoppingBag className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Order History</h1>
          </div>
          <p className="text-gray-600">View and manage your photo purchases</p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-6"
          />
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="medium"
                icon={<Filter className="w-4 h-4" />}
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(activeFiltersCount > 0 && 'border-primary-500 text-primary-600')}
              >
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>

              <Button
                variant="outline"
                size="medium"
                icon={<SortDesc className="w-4 h-4" />}
                onClick={() => toggleSort('createdAt')}
              >
                Sort
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status || ''}
                    onChange={e => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={e => handleFilterChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={e => handleFilterChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="medium"
                    icon={<X className="w-4 h-4" />}
                    onClick={clearFilters}
                    className="w-full"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Orders List */}
        {loading && orders.length > 0 && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="small" />
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500 mb-6">
              {activeFiltersCount > 0
                ? 'Try adjusting your filters to see more results.'
                : "You haven't placed any orders yet."}
            </p>
            <div className="flex gap-3 justify-center">
              {activeFiltersCount > 0 && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
              <Button onClick={() => navigate(ROUTES.EVENTS)}>Browse Photos</Button>
            </div>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <>
            <div className="space-y-3 mb-6">
              {orders.map(order => (
                <div
                  key={order.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Order Header */}
                  <button
                    onClick={() =>
                      setExpandedOrderId(expandedOrderId === order.id ? null : order.id)
                    }
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-4 mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Order #{order.id.substring(0, 8).toUpperCase()}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatDate(order.createdAt)} ({formatRelativeDate(order.createdAt)})
                          </p>
                        </div>
                      </div>
                      {order.event && (
                        <p className="text-sm text-gray-600">{order.event.title}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-6 ml-4 flex-shrink-0">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(order.total)}</p>
                        <div className="mt-1">
                          {getStatusBadge(order.status as OrderStatus)}
                        </div>
                      </div>
                      <ChevronDown
                        size={20}
                        className={`text-gray-400 transition-transform ${
                          expandedOrderId === order.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expandable Order Details */}
                  {expandedOrderId === order.id && (
                    <div className="border-t border-gray-200 px-6 py-6 bg-gray-50 space-y-6">
                      {/* Items List */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4">
                          Items ({order.items.length})
                        </h4>
                        <div className="space-y-3">
                          {order.items.map((item: OrderItem, index: number) => (
                            <div
                              key={index}
                              className="bg-white rounded-lg p-4 border border-gray-100"
                            >
                              <div className="flex gap-4">
                                {item.thumbnail && (
                                  <img
                                    src={item.thumbnail}
                                    alt={item.title}
                                    className="w-20 h-20 object-cover rounded flex-shrink-0"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium text-gray-900 mb-1 truncate">
                                    {item.title}
                                  </h5>
                                  <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                                    <span>
                                      {item.resolution} • Qty: {item.quantity}
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {formatCurrency(item.price * item.quantity)}
                                    </span>
                                  </div>

                                  {/* Download Button */}
                                  {isOrderDownloadable(order) && (
                                    <Button
                                      variant="outline"
                                      size="small"
                                      onClick={() =>
                                        handleDownloadPhoto(
                                          order.id,
                                          (item as any).photoId || '',
                                          item.title
                                        )
                                      }
                                      loading={downloadingPhotoId === (item as any).photoId}
                                      disabled={downloadingPhotoId !== null}
                                      icon={<Download size={14} />}
                                    >
                                      {downloadingPhotoId === (item as any).photoId
                                        ? 'Downloading...'
                                        : 'Download'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Order Summary */}
                      <div className="bg-white rounded-lg p-4 border border-gray-100">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="text-gray-900 font-medium">
                              {formatCurrency(order.subtotal)}
                            </span>
                          </div>
                          {order.discount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Discount</span>
                              <span className="text-green-600 font-medium">
                                -{formatCurrency(order.discount)}
                              </span>
                            </div>
                          )}
                          <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                            <span className="text-gray-900">Total</span>
                            <span className="text-gray-900">{formatCurrency(order.total)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Order Status Info */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-blue-800">
                            {order.status === 'completed' && (
                              <>
                                <strong>Order complete:</strong> Your photos are ready to download
                                anytime. You have lifetime access to all purchased files.
                              </>
                            )}
                            {order.status === 'paid' && (
                              <>
                                <strong>Payment received:</strong> Your order is being processed.
                                Download links will be available shortly.
                              </>
                            )}
                            {order.status === 'pending' && (
                              <>
                                <strong>Pending payment:</strong> Complete the payment to access your
                                photos.
                              </>
                            )}
                            {order.status === 'processing' && (
                              <>
                                <strong>Processing:</strong> Your order is being prepared. Check back
                                soon for download links.
                              </>
                            )}
                            {order.status === 'cancelled' && (
                              <>
                                <strong>Order cancelled:</strong> This order is no longer available.
                                Contact support if you have questions.
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-sm text-gray-600">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} orders
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="small"
                    icon={<ChevronLeft className="w-4 h-4" />}
                    onClick={() => goToPage(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={clsx(
                            'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                            page === pagination.page
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          )}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="small"
                    icon={<ChevronRight className="w-4 h-4" />}
                    iconPosition="right"
                    onClick={() => goToPage(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Help Section */}
        {!loading && orders.length > 0 && (
          <div className="mt-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Need Help?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="font-medium text-gray-900 mb-2">Download Issues</p>
                <p className="text-gray-600">
                  Check your browser's download folder. Files are typically named with the photo
                  title and resolution.
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-2">Order Status</p>
                <p className="text-gray-600">
                  Once your payment is confirmed, your order will be processed within 24 hours.
                  Refresh this page for the latest status updates.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderHistoryPage;
